/**
 * Dashboard Sharing Store
 *
 * Manages dashboard sharing, user permissions, and role management.
 * Extracted from dashboards.js for better separation of concerns.
 */

import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import flatMap from 'lodash/flatMap';
import { DashboardAPI } from '@/apis';
import * as AuthAPI from '@/apis/auth';
import { useAuthStore } from '@/stores/auth';
import { useTrackingStore } from '@/stores/tracking';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { useDashboardsStore } from '@/stores/dashboards';
import { useDashboardTrackingProps } from '@/app/dashboards/composables/useDashboardTrackingProps';
import { filterAndSortUsers } from '@/app/dashboards/utils/sharing.utils';
import constants from '@/app/dashboards/constants';
import type { Identity, Brand, Dashboard, Report, BrandUser } from '@/types/auth.types';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface DashboardUser {
  user?: BrandUser;
  user_role?: string;
}

interface CombinedUser extends BrandUser {
  user_role?: string;
}

/**
 * Type for the parts of dashboardsStore we access.
 * This avoids circular dependency issues with importing the actual store type.
 */
interface DashboardsStoreSlice {
  currentDashboard: Dashboard | null;
  currentDashboardId: number | undefined;
  reports: Record<string, Report>;
  industries: unknown[];
  dashboardIdentityBrands: Brand[];
  clearCurrentDashboard: (params: { identity: Identity | null }) => void;
  refreshDashboard: () => Promise<void>;
  removeDashboardsFromList: (params: {
    dashboardIds: (number | undefined)[];
    identity: Identity | null;
  }) => void;
  getDashboards: () => Promise<void>;
}

/**
 * Type for the parts of authStore we access.
 */
interface AuthStoreSlice {
  identity: Identity | null;
}

// -----------------------------------------------------------------------------
// Store Definition
// -----------------------------------------------------------------------------

export const useDashboardSharingStore = defineStore('dashboardSharing', () => {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const _currentDashboardUsers = ref<DashboardUser[]>([]);
  const shareFormShow = ref(false);
  const dashboardSharableUsers = ref<BrandUser[]>([]);
  const dashboardShareUserSearchTerm = ref('');
  const dashboardUserUpdating = ref(false);

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  const currentDashboardUsers = computed<CombinedUser[]>(() => {
    const combinedUsers = _currentDashboardUsers.value.map((dashboardUser) => {
      const combinedUser: CombinedUser = { ...(dashboardUser.user || {}) };
      if (dashboardUser.user_role) {
        combinedUser.user_role = dashboardUser.user_role;
      }
      return combinedUser;
    });
    return filterAndSortUsers(combinedUsers);
  });

  const currentDashboardUser = computed<CombinedUser | undefined>(() => {
    const authStore = useAuthStore() as unknown as AuthStoreSlice;
    const currentUserId = authStore.identity?.id;
    return currentDashboardUsers.value?.find((user) => user.id === currentUserId);
  });

  const currentDashboardRole = computed<string | undefined>(() => {
    return currentDashboardUser.value?.user_role;
  });

  const isOwner = computed<boolean>(() => {
    return currentDashboardRole.value === constants.ROLES.OWNER.value;
  });

  const isEditor = computed<boolean>(() => {
    return currentDashboardRole.value === constants.ROLES.EDITOR.value;
  });

  const isViewer = computed<boolean>(() => {
    return currentDashboardRole.value === constants.ROLES.VIEWER.value;
  });

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  function launchShareForm() {
    shareFormShow.value = true;
  }

  function closeShareForm() {
    shareFormShow.value = false;
  }

  function setIsDashboardUserUpdating(newValue: boolean) {
    dashboardUserUpdating.value = newValue;
  }

  function setDashboardShareableUsers(users: BrandUser[]) {
    dashboardSharableUsers.value = users;
  }

  function updateDashboardShareUserSearchTerm(searchTerm: string) {
    dashboardShareUserSearchTerm.value = searchTerm;
  }

  function updateCurrentDashboardUsers(users: DashboardUser[]) {
    _currentDashboardUsers.value = Array.isArray(users) ? users : [];
  }

  function clearCurrentDashboardUsers() {
    _currentDashboardUsers.value = [];
  }

  async function removeDashboardUser({ user }: { user: CombinedUser }) {
    const authStore = useAuthStore() as unknown as AuthStoreSlice;
    // NOTE: Lazy import to avoid circular dependency - dashboards.js imports this store
    const dashboardsStore = useDashboardsStore() as unknown as DashboardsStoreSlice;
    const identity = authStore.identity;
    const currentDashboard = dashboardsStore.currentDashboard;
    const currentDashboardId = currentDashboard?.id;
    const removingOwnAccess = identity?.id === user?.id;

    await DashboardAPI.deleteDashboardUser({
      dashboardId: currentDashboard?.id,
      userId: user?.id,
    });

    useTrackingStore().track('Dashboard Remove User', {
      dashboardId: currentDashboardId,
      dashboardName: currentDashboard?.name,
      userId: user?.id,
      removeOwnAccess: removingOwnAccess,
    });

    if (removingOwnAccess) {
      dashboardsStore.clearCurrentDashboard({ identity });
    }

    await dashboardsStore.refreshDashboard();
  }

  async function leaveDashboard() {
    const authStore = useAuthStore() as unknown as AuthStoreSlice;
    // NOTE: Lazy import to avoid circular dependency - dashboards.js imports this store
    const dashboardsStore = useDashboardsStore() as unknown as DashboardsStoreSlice;
    const identity = authStore.identity;
    const leavingDashboardId = dashboardsStore.currentDashboard?.id;

    await removeDashboardUser({
      user: identity as CombinedUser,
    });

    dashboardsStore.removeDashboardsFromList({
      dashboardIds: [leavingDashboardId],
      identity,
    });
  }

  async function getAllAccessibleBrandsUsers({
    search = null,
  }: { search?: string | null } = {}): Promise<BrandUser[]> {
    const authStore = useAuthStore() as unknown as AuthStoreSlice;
    const identity = authStore.identity;
    const accessibleBrands = identity?.accessible_brands ?? [];

    const responses = await Promise.all(
      accessibleBrands.map((brandId) =>
        // Note: AuthAPI.getBrandUsers is untyped JavaScript - params cast needed for TS compatibility
        AuthAPI.getBrandUsers({ brandId, limit: 1000, search } as Parameters<
          typeof AuthAPI.getBrandUsers
        >[0]),
      ),
    );

    return flatMap(responses, (response) => (response?.data?.data ?? []) as BrandUser[]);
  }

  async function getDashboardSharableUsers({
    search = null,
  }: { search?: string | null } = {}): Promise<BrandUser[]> {
    const brandUsers = await getAllAccessibleBrandsUsers({ search });

    const uniqueUsers: Record<string, boolean> = {};
    const uniqueBrandUsers: BrandUser[] = [];

    brandUsers.forEach((brandUser) => {
      if (brandUser.email && !(brandUser.email in uniqueUsers)) {
        uniqueBrandUsers.push(brandUser);
        uniqueUsers[brandUser.email] = true;
      }
    });

    setDashboardShareableUsers(uniqueBrandUsers);
    return uniqueBrandUsers;
  }

  async function refreshDashboardUsers() {
    // NOTE: Lazy import to avoid circular dependency - dashboards.js imports this store
    const dashboardsStore = useDashboardsStore() as unknown as DashboardsStoreSlice;
    const dashboardId = dashboardsStore.currentDashboardId;
    const response = await DashboardAPI.getDashboardUsers({ dashboardId });
    const users = response?.data;
    updateCurrentDashboardUsers(users);
  }

  async function shareDashboard({ user, role }: { user: CombinedUser; role: string }) {
    // NOTE: Lazy import to avoid circular dependency - dashboards.js imports this store
    const dashboardsStore = useDashboardsStore() as unknown as DashboardsStoreSlice;
    const dashboardReportsStore = useDashboardReportsStore();
    const currentDashboard = dashboardsStore.currentDashboard;

    const previousRole = currentDashboardUsers.value?.find((u) => u.id === user?.id)?.user_role;

    await DashboardAPI.shareDashboard({
      dashboardId: currentDashboard?.id,
      userId: user?.id,
      userRole: role,
    });

    const { reportDateRange, contextDateRange } = storeToRefs(dashboardReportsStore);

    const { dashboardTrackingProps } = useDashboardTrackingProps({
      dashboard: currentDashboard,
      reportDateRange: reportDateRange.value,
      contextDateRange: contextDateRange.value,
      reports: dashboardsStore.reports,
      industries: dashboardsStore.industries,
      brands: dashboardsStore.dashboardIdentityBrands,
    });

    useTrackingStore().track('Dashboard Share User', {
      ...dashboardTrackingProps.value,
      userId: user?.id,
      role,
      previousRole,
    });

    await dashboardsStore.refreshDashboard();
  }

  async function transferOwnership({ user }: { user: CombinedUser }) {
    // NOTE: Lazy import to avoid circular dependency - dashboards.js imports this store
    const dashboardsStore = useDashboardsStore() as unknown as DashboardsStoreSlice;
    const currentDashboard = dashboardsStore.currentDashboard;
    const currentDashboardId = currentDashboard?.id;

    const response = await DashboardAPI.updateDashboardOwner({
      dashboardId: currentDashboard?.id,
      userId: user?.id,
    });

    useTrackingStore().track('Dashboard Transfer Ownership', {
      dashboardId: currentDashboardId,
      dashboardName: currentDashboard?.name,
      userId: user?.id,
      previousUserId: currentDashboard?.user_id,
    });

    await dashboardsStore.refreshDashboard();
    await dashboardsStore.getDashboards();

    return response?.data;
  }

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    // State
    _currentDashboardUsers,
    shareFormShow,
    dashboardSharableUsers,
    dashboardShareUserSearchTerm,
    dashboardUserUpdating,

    // Getters
    currentDashboardUsers,
    currentDashboardUser,
    currentDashboardRole,
    isOwner,
    isEditor,
    isViewer,

    // Actions
    launchShareForm,
    closeShareForm,
    setIsDashboardUserUpdating,
    setDashboardShareableUsers,
    updateDashboardShareUserSearchTerm,
    updateCurrentDashboardUsers,
    clearCurrentDashboardUsers,
    removeDashboardUser,
    leaveDashboard,
    getAllAccessibleBrandsUsers,
    getDashboardSharableUsers,
    refreshDashboardUsers,
    shareDashboard,
    transferOwnership,
  };
});
